import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../../services/api'
import CycleDocumentViewer from './CycleDocumentViewer'
import { formatDate } from '../cycleStatus'

// VH-228 phase 0: the documents stored in a cycle (health report, weekly plans) and the one being read.
export function CycleDocumentsTab({ cycleId }) {
  const { t, i18n } = useTranslation()
  const [selectedId, setSelectedId] = useState(null)
  const query = useQuery({
    queryKey: ['cycleDocuments', cycleId],
    queryFn: () => healthCyclesAPI.listDocuments(cycleId),
    select: (response) => response.data,
    enabled: !!cycleId,
  })

  if (query.isLoading) return <p className="text-sm text-gray-500">{t('cycleDocuments.loading')}</p>
  if (query.isError) return <p className="text-sm text-red-600" role="alert">{t('cycleDocuments.loadFailed')}</p>
  const documents = query.data ?? []
  if (documents.length === 0) {
    return <p className="text-sm text-gray-500 py-6 text-center">{t('cycleDocuments.empty')}</p>
  }
  const activeId = selectedId ?? (documents.find((d) => d.kind === 'health_report') ?? documents[0]).id

  return (
    <div className="space-y-5">
      <ul className="flex flex-wrap gap-2" aria-label={t('cycleDocuments.listLabel')}>
        {documents.map((doc) => (
          <li key={doc.id}>
            <button type="button" onClick={() => setSelectedId(doc.id)} aria-pressed={doc.id === activeId}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm ${
                doc.id === activeId ? 'border-purple-600 bg-purple-50 text-purple-900' : 'border-gray-200 bg-surface-container text-gray-700 hover:border-gray-300'
              }`}>
              <DocumentTextIcon className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <span className="block font-medium">{t(`cycleDocuments.kinds.${doc.kind}`)}</span>
                <span className="block text-xs text-gray-500">
                  {doc.week_start ? t('cycleDocuments.weekOf', { date: formatDate(doc.week_start, i18n.language) }) : doc.title}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <CycleDocumentViewer cycleId={cycleId} documentId={activeId} />
    </div>
  )
}

export default CycleDocumentsTab
